import { Injectable } from "@angular/core";
import { IReceivePayment, IReceivePaymentCreditMemo, IReceivePaymentDetail } from "../../../modules/shared/components/receive-payments/model/receive-payment.model";
import { Status } from "../../constants/constants";
import { SOChannel } from "../constants/constants";

@Injectable({
    providedIn: "root",
})
export class ReceivedPaymentRequestBuilderService {
    
    constructor() { }

    prepareReceivedPaymentRequestData(data: any): IReceivePayment {
        let creditMemos = this.prepareReceivePaymentCreditMemoData(data);
        let appliedCreditMemoTotal = creditMemos?.reduce((total, d) => total + Number(d.appliedCreditAmount), 0);
        const receivePayment: IReceivePayment = {
            id: data?.receivedPayment?.id ?? 0,
            createdSource: data?.app.userIP,
            updatedSource: data?.app.userIP,
            createdBy: data?.app.user.userId,
            updatedBy: data?.app.user.userId,
            createdDate: data?.app._saveDateTimeWithZone(new Date()),
            updatedDate: data?.app._saveDateTimeWithZone(new Date()),
            number: data?.detailsData?.number ?? "",
            description: "",
            organizationId: data?.app.companyId,
            accountId: data?.detailsData?.accountId ?? 0,
            paymentMethodId: data?.detailsData?.paymentMethodId ?? 0,
            paymentTermId: data?.detailsData?.paymentTermId ?? 0,
            receivedPaymentDate: data?.app._saveDateTimeWithZone(new Date()),
            totalReceivedAmount: appliedCreditMemoTotal,
            memo: "",
            currencyUnitId: 0,
            discountId: 0,
            discountTypeId: 0,
            discount: 0,
            referenceNumber: "",
            statusId: Status.Active,
            isValid: true,
            validFromDate: data?.app._saveDateTimeWithZone(new Date()),
            validToDate: data?.app._saveDateTimeWithZone(new Date()),
            receivedPaymentNote: null,
            receivedPaymentAudit: null,
            financialAccountId: 0,
            classId: 0,
            isWriteOff: false,
            receivedPaymentDetail: this.prepareReceivedPaymentDetailsData(data, appliedCreditMemoTotal),
            receivedPaymentCreditMemo: creditMemos,
            customAttributeValues: null,
            businessName: data?.detailsData?.account?.businessName ?? "",
            channelId: data?.detailsData?.channelId ?? 0,
            overPaymentAmount: 0,
            pendingOverPaymentAmount: 0,
            paymentTypeId: 1,
            isTransaction: false,
        };
        return receivePayment;
    }

    private prepareReceivePaymentCreditMemoData(data: any): IReceivePaymentCreditMemo[] {
        const usedCredits = data?.selectedCredits?.filter((c: any) => c.isSelected == true);
        const unUsedCredits = data?.selectedCredits?.filter((c: any) => c.isSelected == false);
        let remainingAmount = 0;
        let totalOfUsedCredits = usedCredits?.reduce((total, d) => total + Number(d.pendingCreditAmount), 0);
        if(data?.detailsData?.netAmount > totalOfUsedCredits){
            remainingAmount = data?.detailsData?.netAmount - totalOfUsedCredits;
        }
        const { applicableCredits } = this.findApplicableCredits(unUsedCredits, remainingAmount);
        const usedReceivedPaymentCreditMemos: IReceivePaymentCreditMemo[] = (usedCredits || []).map(credit => {
            let foundCredit = data?.receivedPayment?.receivedPaymentCreditMemo?.find(c => c.creditMemoId == credit.id);
            return {
                id: foundCredit && foundCredit?.id ? foundCredit.id : 0,
                createdSource: data?.app.userIP,
                updatedSource: data?.app.userIP,
                createdBy: data?.app.user.userId,
                updatedBy: data?.app.user.userId,
                createdDate: data?.app._saveDateTimeWithZone(new Date()),
                updatedDate: data?.app._saveDateTimeWithZone(new Date()),
                receivePaymentId: foundCredit && foundCredit?.receivedPaymentId ? foundCredit.receivedPaymentId : 0,
                creditMemoId: credit.id,
                invoiceId: data?.channel == SOChannel.INVOICING ? data?.detailsData?.id : 0,
                appliedCreditAmount: credit.pendingCreditAmount,
                statusId: Status.Active,
                salesOrderId: data?.channel == SOChannel.SALES_ORDER ? data?.detailsData?.id : 0,
                overPaymentId: credit?.overPaymentId ?? 0,
                txnId: credit?.typeId != 3 && credit?.typeId != 2 ? credit?.id : 0,
            };
        });
        const unusedReceivedPaymentCreditMemos: IReceivePaymentCreditMemo[] = (applicableCredits || []).map(credit => {
            return {
                id: 0,
                createdSource: data?.app.userIP,
                updatedSource: data?.app.userIP,
                createdBy: data?.app.user.userId,
                updatedBy: data?.app.user.userId,
                createdDate: data?.app._saveDateTimeWithZone(new Date()),
                updatedDate: data?.app._saveDateTimeWithZone(new Date()),
                receivePaymentId: 0,
                creditMemoId: credit.id,
                invoiceId: data?.channel == SOChannel.INVOICING ? data?.detailsData?.id : 0,
                appliedCreditAmount: credit.pendingCreditAmount,
                statusId: Status.Active,
                salesOrderId: data?.channel == SOChannel.SALES_ORDER ? data?.detailsData?.id : 0,
                overPaymentId: credit?.overPaymentId ?? 0,
                txnId: credit?.typeId != 3 && credit?.typeId != 2 ? credit?.id : 0,
            };
        });
        const receivedPaymentCreditMemos = [...usedReceivedPaymentCreditMemos, ...unusedReceivedPaymentCreditMemos];
        return receivedPaymentCreditMemos;
    }

    findApplicableCredits(items: any[], target: number): { applicableCredits: any[] } | null {     
        let sortedItems = items.sort((a, b) => b.pendingCreditAmount - a.pendingCreditAmount);
        const result: any[] = [];     
        let currentSum = 0;    
        for (const item of sortedItems) {         
            if (currentSum >= target) break;         
            const amountToUse = Math.min(item.pendingCreditAmount, target - currentSum);         
            currentSum += amountToUse;         
            if (amountToUse > 0) {            
                result.push({ ...item, pendingCreditAmount: amountToUse, id: item?.type?.toLowerCase() == "payment" ? 0 : item?.id, overPaymentId: item?.type?.toLowerCase() == "payment" ? item?.id : 0 });             
            }     
        }     
        return { applicableCredits: result };     
    } 

    private prepareReceivedPaymentDetailsData(data: any, appliedCreditMemoTotal: number): IReceivePaymentDetail[] {
        let foundReceivedPaymentDetail = data?.receivedPayment?.receivedPaymentDetail?.find(d => d?.receivedPaymentId == data?.receivedPayment?.id);
        const receivePaymentDetail: IReceivePaymentDetail = {
            id: foundReceivedPaymentDetail && foundReceivedPaymentDetail?.id ? foundReceivedPaymentDetail.id : 0,
            createdSource: data?.app.userIP,
            updatedSource: data?.app.userIP,
            createdBy: data?.app.user.userId,
            updatedBy: data?.app.user.userId,
            createdDate: data?.app._saveDateTimeWithZone(new Date()),
            updatedDate: data?.app._saveDateTimeWithZone(new Date()),
            receivedPaymentId: data?.receivedPayment?.id ?? 0,
            invoiceId: data?.channel == SOChannel.INVOICING ? data?.detailsData?.id : 0,
            invoiceAmount: data?.detailsData?.netAmount,
            invoiceReceivedAmount: 0,
            contactId: data?.detailsData?.contactId ?? 0,
            invoicePendingAmount: Number(data?.detailsData?.netAmount) - appliedCreditMemoTotal,
            statusId: Status.Active,
            appliedCredit: appliedCreditMemoTotal,
            writeOffAmount: 0,
            txnId: 0,
            txnNumber: "",
            salesOrderId: data?.channel == SOChannel.SALES_ORDER ? data?.detailsData?.id : 0,
            salesOrderNumber: data?.channel == SOChannel.SALES_ORDER ? data?.detailsData?.number : "",
        };
        return [receivePaymentDetail];
    }
}